import React, { Fragment, Component } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  Back,
  Button,
  Frame,
  DataGrid,
  DataGridFooter,
  Search,
  Body,
  Thirds,
  Left,
  Right,
  Center,
  palette,
  spacing,
} from "@conserveit/blocks";
import { withRouter } from "react-router";
import styled from "styled-components";
import { Trans } from "@lingui/macro";
import TriageActionModal from "../../containers/_BETA/TriageActionModal";
import NoResultsOverlay from "../NoResultsOverlay";
import ToggleSnoozed from "../../containers/ToggleSnoozed";
import InfoText from "./components/InfoText";
import EquipHeader from "./components/EquipHeader";
import EquipRow from "./components/EquipRow";
import EquipActions from "./components/EquipActions";
import { equipColumns } from "../../utils/columns";
import { history } from "../../browserHistory";
import { searchedEvent, viewedInsightEvent } from "../../utils/eventTracking";
import { InsightType } from "../../utils/routes";
import { connect } from "react-redux";

const DEFAULT_ROWS_TO_SHOW = 25;

const GoBack = styled(Link)`
  text-decoration: none;
`;
const SEARCH_KEY = "equipsSearch";

const StyledBody = styled(Body)`
  color: ${palette.neutral8};
`;

const StyledThirds = styled(Thirds)`
  margin-bottom: ${spacing.default};
`;

const StyledFrame = styled(Frame)`
  position: relative;
  overflow: visible;
`;

const StyledDataGrid = styled(DataGrid)`
  overflow: visible;
`;

function mapStateToProps(state) {
  console.log("mapStateToProps user: ", state.auth.user)
  return {
    user: state.auth.user,
  };
}

function calcAllSelected(visible, selected) {
  const selectableIds = visible
    .filter((row) => row.issueKey === undefined)
    .map((row) => row.equipRuleId);
  // If there's nothing to select...
  if (selectableIds.length < 1) return false;

  // Otherwise, check whether each selectable row equipRuleId
  // already exists in state.selected.  If it does, return true, otherwise return false.
  return selectableIds.reduce((isSelected, id) => {
    if (isSelected === false) return false;
    return selected.has(id);
  }, true);
}

class Equip extends Component {
  static propTypes = {
    sortOption: PropTypes.string.isRequired,
    sortDirection: PropTypes.string.isRequired,
    showSnoozed: PropTypes.bool,
    insightType: PropTypes.string,
    hits: PropTypes.array,
    ruleName: PropTypes.string,
    syncoId: PropTypes.string,
    activeSite: PropTypes.string,
    ruleId: PropTypes.string,
    userAccess: PropTypes.array,
    updateSort: PropTypes.func,
    showModal: PropTypes.func,
    urlHistory: PropTypes.object,
    selectedSites: PropTypes.array,
    user: PropTypes.object,
  };

  static defaultProps = {
    showSnoozed: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      noResults: false,
      hits: [],
      activeHits: [],
      filtered: [],
      visible: [],
      selected: new Set(),
      isAllSelected: false,
      isAllSelectedDisabled: false,
      // eslint-disable-next-line
      rowsToShow: DEFAULT_ROWS_TO_SHOW,
      snoozedCount: props.hits.filter((rule) => rule.isAllSnoozed).length,
      query: undefined,
    };
  }

  componentDidMount() {
    const { showSnoozed, hits } = this.props;
    const activeHits = showSnoozed
      ? hits
      : hits.filter((asset) => !asset.isSnoozed);

    // Track Equip view metrics
    viewedInsightEvent(hits);

    this.setState(() => ({
      hits: activeHits || [],
      activeHits,
      filtered: activeHits || [],
      visible: activeHits.filter((asset, i) => i < DEFAULT_ROWS_TO_SHOW),
      rowsToShow: DEFAULT_ROWS_TO_SHOW,
      noResults: activeHits.length === 0,
      snoozedCount: hits.filter((asset) => asset.isSnoozed).length,
    }));
  }

  componentDidUpdate(
    { hits, showSnoozed, sortOption, sortDirection },
    { visible }
  ) {
    if (visible !== this.state.visible) {
      const selectableIds = this.state.visible
        .filter((row) => row.issueKey === undefined)
        .map((row) => row.equipRuleId);
      if (selectableIds.length === 0) {
        this.handleDisableSelectedAll();
      } else {
        this.handleEnableSelectedAll();
      }
    }

    // If the data set has changed, update `hits` in state and clear all selections
    if (hits !== this.props.hits && this.props.hits.length > 0) {
      this.handleSetHits({ hits: this.props.hits, clearSelected: true });
    }

    // If show/hide snooze is toggled, update `hits` in state without clearing selections
    if (showSnoozed !== this.props.showSnoozed) {
      this.handleUpdateShowSnoozed({
        showSnoozed: this.props.showSnoozed,
        hits: this.props.hits,
      });
    }

    if (
      sortOption !== this.props.sortOption ||
      sortDirection !== this.props.sortDirection
    ) {
      this.handleUpdateData({
        query: this.state.searchString,
        filtered: this.state.filtered,
      });
    }
  }

  handleUpdateShowSnoozed = ({ showSnoozed, hits }) => {
    const activeHits = showSnoozed
      ? hits
      : hits.filter((asset) => !asset.isSnoozed);
    this.setState(
      {
        activeHits,
      },
      () => {
        this.handleUpdateData(
          { filtered: activeHits, query: this.state.searchString },
          false
        );
      }
    );
  };

  handleUpdateData = ({ filtered = [], query }, trackEvent = true) => {
    // Segment tracking - Searched hits
    if (trackEvent) {
      searchedEvent("hits", query);
    }

    if (filtered.length === 0) {
      this.setState({
        noResults: true,
      });
      return;
    }

    const sorted = this.sortRows(filtered);

    this.setState(({ selected = [], rowsToShow }) => {
      const visible = sorted.slice(0, rowsToShow);
      const isAllSelected = calcAllSelected(visible, selected, filtered);

      return {
        filtered: sorted,
        visible: sorted.slice(0, rowsToShow),
        isAllSelected,
        rowsToShow,
        noResults: false,
        query,
      };
    });
  };

  /**
   * Use the library of sort functions defined in the sort by component for all
   * the rules
   * @param {*} rows
   */
  sortRows(rows) {
    const { sortOption, sortDirection } = this.props;
    const column = equipColumns[sortOption];
    return column.sortFn(rows, sortDirection);
  }

  handleSetHits({ hits, clearSelected = false }) {
    const snoozedHits = hits.filter((hit) => hit.isSnoozed);
    const activeHits = this.props.showSnoozed
      ? hits
      : hits.filter((hit) => !hit.isSnoozed);

    this.setState((state) => {
      const sorted = this.sortRows(activeHits);
      const selected = clearSelected ? new Set() : state.selected;
      const isAllSelected = clearSelected ? false : state.isAllSelected;

      return {
        hits: sorted,
        filtered: sorted,
        visible: sorted.slice(0, state.rowsToShow),
        snoozedCount: snoozedHits.length,
        selected,
        isAllSelected,
        noResults: sorted.length < 1,
      };
    });
  }
  handleDisableSelectedAll = () => {
    this.setState({
      isAllSelectedDisabled: true,
    });
  };
  handleEnableSelectedAll = () => {
    this.setState({
      isAllSelectedDisabled: false,
    });
  };
  handleSelectAll = () => {
    this.setState(({ visible, selected }) => {
      const selectableIds = visible
        .filter((row) => Boolean(row.issueKey) !== true)
        .map((row) => row.equipRuleId);
      // If nothing's selectable, don't allow selectAll
      if (selectableIds.length < 1) {
        return {
          isAllSelected: false,
        };
      }

      // Otherwise, if all are selected, unselect all visible
      const allSelected = calcAllSelected(visible, selected);
      if (allSelected) {
        const clonedSet = new Set(selected);
        selectableIds.forEach((ruleEquipId) => {
          clonedSet.delete(ruleEquipId);
        });

        return {
          isAllSelected: false,
          selected: clonedSet,
        };
      }

      // Otherwise, add all visible to selected
      const clonedSet = new Set(selected);
      selectableIds.forEach((id) => {
        clonedSet.add(id);
      });
      return {
        isAllSelected: true,
        selected: clonedSet,
      };
    });
  };

  handleRowSelect = (id) => () => {
    this.setState(({ selected, visible }) => {
      const clonedSet = new Set(selected);
      if (selected.has(id)) {
        clonedSet.delete(id);
      } else {
        clonedSet.add(id);
      }

      return {
        selected: clonedSet,
        isAllSelected: calcAllSelected(visible, clonedSet),
      };
    });
  };

  handleShowMore = () => {
    this.setState((state) => {
      const updatedRowsToShow = state.rowsToShow + 10;
      return {
        rowsToShow: updatedRowsToShow,
        visible: state.filtered.filter((asset, i) => i < updatedRowsToShow),
        isAllSelected: false,
      };
    });
  };

  handleShowAll = () => {
    this.setState((state) => ({
      visible: [...state.filtered],
      rowsToShow: state.filtered.length,
      isAllSelected: false,
    }));
  };

  handleAssociate =
    (targetRuleId) =>
    (createNew = false) => {
      this.props.showModal({
        selected: [targetRuleId],
        hits: this.state.hits,
        createNew,
      });
    };

  handleCreateAndAssociate = () => {
    const equips = this.state.hits;
    const { site } = equips[0];
    history.push("/actions/add", {
      rule: this.props.ruleName,
      equips: equips
        .filter((h) => this.state.selected.has(h.equipRuleId))
        .map((h) => ({
          equipRuleId: h.equipRuleId,
          equipName: h.targetName,
        })),
      site: site.id,
    });
  };

  render() {
    const {
      filtered,
      visible,
      selected,
      isAllSelected,
      noResults,
      isAllSelectedDisabled,
    } = this.state;
    const filteredCount = filtered.length;
    const visibleCount = visible.length;
    const selectedCount = selected.size;
    const hideEdits = !this.props.user.access.includes("editIssue");

    const { ruleName, sortDirection, urlHistory, syncoId, insightType, user } =
      this.props;

    let backToPath = urlHistory.insightsHistory.path;
    if (insightType === InsightType.Monitoring) {
      backToPath = urlHistory.monitoringHistory.path;
    }

    return (
      <Fragment>
        <InfoText ruleName={ruleName} />
        <StyledThirds>
          <Left>
            <GoBack to={backToPath}>
              <Back text="Back" /> {/* FIXME: i18n */}
            </GoBack>
          </Left>
          <Center>
            <Search
              sessionKey={SEARCH_KEY}
              data={this.state.activeHits}
              initial={this.state.query}
              onSearch={this.handleUpdateData}
              indexId="occurrenceId"
              defaultField="targetValueString"
              placeholder="Filter results e.g. ‘critical’ or ‘flat lining’"
              thick
              cy="Equips-Search"
            />
          </Center>
          <Right>
            <ToggleSnoozed count={this.state.snoozedCount} />
          </Right>
        </StyledThirds>
        <StyledFrame>
          {selected.size > 0 && (
            <EquipActions
              linkTicket={this.props.showModal([...selected], this.state.hits)}
              createTicket={this.handleCreateAndAssociate}
              equipRuleIds={[...this.state.selected]}
              isHidden={hideEdits}
            />
          )}
          <StyledDataGrid
            group={[{ start: 1, end: 8 }]}
            columns={8}
            grow={[5]}
            cellProps={{ type: "dense", density: "default" }}
          >
            <EquipHeader
              onSort={this.props.updateSort}
              activeSort={this.props.sortOption}
              sortDirection={sortDirection}
              filtered={filtered}
              hits={this.state.hits}
              isAllSelected={isAllSelected}
              onSelectAll={this.handleSelectAll}
              isAllSelectedDisabled={isAllSelectedDisabled}
              hideEdits={hideEdits}
            />
            {noResults && <NoResultsOverlay noResultsMessage />}

            {filtered.length > 0 &&
              visible.map((asset) => (
                <EquipRow
                  asset={asset}
                  ruleName={ruleName}
                  isSelected={selected.has(asset.equipRuleId)}
                  isAssociated={Boolean(
                    asset.issueKey
                  )} /* HACK: Cast as falsey if empty/undefined/null */
                  onSelect={this.handleRowSelect(asset.equipRuleId)}
                  key={asset.equipRuleId}
                  onAssociate={this.handleAssociate(asset.targetRuleId)}
                  hideEdits={hideEdits}
                />
              ))}

            <DataGridFooter>
              <StyledBody>
                <Trans id="show_more_row.showing_count">
                  Showing <strong>{visibleCount}</strong> of{" "}
                  <strong>{filteredCount}</strong>
                </Trans>
                {/* hack to create an empty space between the filter number and the following text */}
                &nbsp;
                {selectedCount > 0 && (
                  <Trans id="show_more_row.hits_selected">
                    Hits ({selectedCount} selected)
                  </Trans>
                )}
              </StyledBody>

              {filteredCount > visibleCount && (
                <Button
                  appearance="outline"
                  primary
                  thin
                  onClick={this.handleShowMore}
                >
                  <Trans id="show_more_row.show_more">Show More</Trans>
                </Button>
              )}

              {filteredCount > visibleCount && (
                <Button
                  appearance="outline"
                  primary
                  thin
                  onClick={this.handleShowAll}
                >
                  <Trans id="show_more_row.show_all">Show all</Trans>
                </Button>
              )}
            </DataGridFooter>
          </StyledDataGrid>
        </StyledFrame>
        <TriageActionModal ruleName={ruleName} syncoId={syncoId} />
      </Fragment>
    );
  }
}

export default connect(mapStateToProps)(Equip);